import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ActionMenu, type ActionMenuEntry } from "@/components/admin/action-menu";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { useSegments } from "@/lib/customers/customers-hooks";
import {
	PUSH_STATUS_LABELS,
	type PushCampaign,
	pushApi,
} from "@/lib/marketing/push-api";

const CLE = ["marketing", "push"] as const;

const nombre = (n: number) => n.toLocaleString("fr-FR");

const dateHeure = (iso: string | null) =>
	iso
		? new Date(iso).toLocaleString("fr-FR", {
				day: "2-digit",
				month: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";

/**
 * Notifications poussées vers l'app mobile.
 *
 * L'écran dit franchement quand le canal n'est pas opérationnel : l'app
 * Flutter n'embarque pas encore Firebase Messaging, et aucun appareil n'est
 * donc enregistré. Laisser croire qu'une notification part serait pire que
 * de ne rien proposer.
 */
export function AdminMarketingPushPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny } = usePermissions();
	const peutEcrire = hasAny("MARKETING.CMS.WRITE");
	const peutEnvoyer = hasAny("MARKETING.CAMPAIGN.SEND");

	const [formulaire, setFormulaire] = useState<PushCampaign | null | "neuf">(null);

	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "list"],
		queryFn: () => pushApi.list(),
	});
	const { data: stats } = useQuery({
		queryKey: [...CLE, "stats"],
		queryFn: () => pushApi.stats(),
	});

	const rafraichir = () => {
		void qc.invalidateQueries({ queryKey: CLE });
	};
	const echec = (e: unknown) => toast.error(extractErrorMessage(e));

	const creer = useMutation({
		mutationFn: (p: {
			title: string;
			message: string;
			deepLink?: string;
			segmentId?: string;
		}) => pushApi.create(p),
		onSuccess: () => {
			toast.success("Notification créée en brouillon");
			setFormulaire(null);
			rafraichir();
		},
		onError: echec,
	});

	const modifier = useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string;
			payload: { title?: string; message?: string; deepLink?: string | null };
		}) => pushApi.update(id, payload),
		onSuccess: () => {
			toast.success("Notification mise à jour");
			setFormulaire(null);
			rafraichir();
		},
		onError: echec,
	});

	const programmer = useMutation({
		mutationFn: ({ id, at }: { id: string; at?: string }) =>
			pushApi.schedule(id, at),
		onSuccess: () => {
			toast.success("Notification programmée");
			rafraichir();
		},
		onError: echec,
	});

	const annuler = useMutation({
		mutationFn: (id: string) => pushApi.cancel(id),
		onSuccess: () => {
			toast.success("Notification déprogrammée");
			rafraichir();
		},
		onError: echec,
	});

	const supprimer = useMutation({
		mutationFn: (id: string) => pushApi.remove(id),
		onSuccess: () => {
			toast.success("Notification supprimée");
			rafraichir();
		},
		onError: echec,
	});

	const menu = (c: PushCampaign): ActionMenuEntry[] => {
		const entrees: ActionMenuEntry[] = [
			{
				label: "Modifier",
				icon: "edit",
				disabled: !peutEcrire || c.status === "sent" || c.status === "sending",
				onClick: () => setFormulaire(c),
			},
		];

		if (c.status === "draft") {
			entrees.push({
				label: "Envoyer maintenant",
				icon: "bell",
				disabled: !peutEnvoyer,
				onClick: () => {
					void confirm({
						title: "Envoyer cette notification ?",
						message: `« ${c.title} » arrivera sur l'écran de ${nombre(stats?.optin ?? 0)} personne(s). C'est immédiat et irréversible.`,
						confirmLabel: "Envoyer",
						tone: "default",
					}).then((ok) => {
						if (ok) programmer.mutate({ id: c.id });
					});
				},
			});
		}

		if (c.status === "scheduled") {
			entrees.push({
				label: "Déprogrammer",
				icon: "clock",
				disabled: !peutEcrire,
				onClick: () => annuler.mutate(c.id),
			});
		}

		if (c.status !== "sent") {
			entrees.push("sep", {
				label: "Supprimer",
				icon: "trash",
				danger: true,
				disabled: !peutEcrire,
				onClick: () => {
					void confirm({
						title: "Supprimer cette notification ?",
						message: `« ${c.title} » sera supprimée.`,
						confirmLabel: "Supprimer",
						tone: "danger",
					}).then((ok) => {
						if (ok) supprimer.mutate(c.id);
					});
				},
			});
		}

		return entrees;
	};

	const campagnes = data?.items ?? [];

	return (
		<>
			<PageHead
				title="Notifications de l'app"
				sub="Messages poussés vers l'application mobile"
				actions={
					peutEcrire && (
						<AdminButton
							variant="primary"
							onClick={() => setFormulaire("neuf")}
						>
							<AdminIcon n="plus" s={14} />
							Nouvelle notification
						</AdminButton>
					)
				}
			/>

			{stats && !stats.senderReady && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="warning"
						title="Le canal n'est pas encore opérationnel"
						description="La clé de service FCM n'est pas configurée sur le serveur (FCM_SERVICE_ACCOUNT_JSON). Les notifications se préparent et restent en attente : rien ne partira avant que la clé soit posée."
					/>
				</div>
			)}

			{stats && stats.devices.total === 0 && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="info"
						title="Aucun appareil enregistré"
						description="L'application mobile n'envoie pas encore son jeton de notification. Le serveur est prêt à les recevoir ; il reste à l'ajouter côté app."
					/>
				</div>
			)}

			{stats && (
				<div className="channel-grid" style={{ marginBottom: 18 }}>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "var(--a-forest)" }}>
								<AdminIcon n="phone" s={18} />
							</div>
							<div>
								<div className="ch-name">Appareils</div>
								<div className="ch-sub">Jetons actifs</div>
							</div>
						</div>
						<div className="ch-v">{nombre(stats.devices.total)}</div>
						<div className="muted" style={{ fontSize: 11 }}>
							{stats.devices.android} Android · {stats.devices.ios} iOS
						</div>
					</div>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "var(--a-terracotta)" }}>
								<AdminIcon n="users" s={18} />
							</div>
							<div>
								<div className="ch-name">Opt-in</div>
								<div className="ch-sub">Ont accepté les notifications</div>
							</div>
						</div>
						<div className="ch-v">{nombre(stats.optin)}</div>
					</div>
					<div className="channel-card">
						<div className="ch-h">
							<div className="ch-ic" style={{ background: "var(--a-ochre)" }}>
								<AdminIcon n="trend" s={18} />
							</div>
							<div>
								<div className="ch-name">Taux d'ouverture</div>
								<div className="ch-sub">Campagnes envoyées</div>
							</div>
						</div>
						<div className="ch-v">
							{stats.clickRate.toFixed(1)}
							<small style={{ fontSize: 14, color: "var(--a-ink-mute)" }}>%</small>
						</div>
					</div>
				</div>
			)}

			<Card>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Notification</th>
								<th>Cible</th>
								<th className="r">Envoyées</th>
								<th className="r">Ouvertures</th>
								<th>Quand</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={7}>
										<div className="empty">Chargement…</div>
									</td>
								</tr>
							)}
							{!isLoading && campagnes.length === 0 && (
								<tr>
									<td colSpan={7}>
										<div className="empty">Aucune notification.</div>
									</td>
								</tr>
							)}
							{campagnes.map((c) => {
								const st = PUSH_STATUS_LABELS[c.status];
								return (
									<tr key={c.id}>
										<td>
											<strong style={{ fontSize: 12.5 }}>{c.title}</strong>
											<div className="muted" style={{ fontSize: 11 }}>
												{c.message}
											</div>
											{c.deepLink && (
												<div
													className="mono muted"
													style={{ fontSize: 10.5, marginTop: 2 }}
												>
													{c.deepLink}
												</div>
											)}
										</td>
										<td className="muted">{c.segmentLabel ?? "Tous opt-in"}</td>
										<td className="r tn">
											{c.sentCount > 0 ? nombre(c.sentCount) : "—"}
										</td>
										<td className="r tn">
											{c.sentCount > 0
												? `${nombre(c.clickCount)} (${c.clickRate.toFixed(1)}%)`
												: "—"}
										</td>
										<td className="muted" style={{ fontSize: 11 }}>
											{dateHeure(c.sentAt ?? c.scheduledAt)}
										</td>
										<td>
											<Tag variant={st.variant}>{st.label}</Tag>
											{c.lastError && (
												<div
													className="muted"
													style={{ fontSize: 10.5, marginTop: 2 }}
												>
													{c.lastError}
												</div>
											)}
										</td>
										<td className="right">
											<ActionMenu items={menu(c)} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>

			{formulaire && (
				<PushFormModal
					campagne={formulaire === "neuf" ? null : formulaire}
					saving={creer.isPending || modifier.isPending}
					onClose={() => setFormulaire(null)}
					onSubmit={(p) => {
						if (formulaire !== "neuf") {
							modifier.mutate({ id: formulaire.id, payload: p });
						} else {
							creer.mutate(p as Parameters<typeof creer.mutate>[0]);
						}
					}}
				/>
			)}
		</>
	);
}

/**
 * Formulaire d'une notification, avec aperçu du bandeau tel qu'il arrivera.
 *
 * Les limites de caractères ne sont pas décoratives : au-delà, Android et iOS
 * coupent le texte, et c'est souvent la fin du message qui portait l'offre.
 */
function PushFormModal({
	campagne,
	onClose,
	onSubmit,
	saving,
}: {
	campagne: PushCampaign | null;
	onClose: () => void;
	onSubmit: (p: {
		title: string;
		message: string;
		deepLink?: string;
		segmentId?: string;
	}) => void;
	saving: boolean;
}) {
	const { data: segments } = useSegments();
	const [titre, setTitre] = useState(campagne?.title ?? "");
	const [message, setMessage] = useState(campagne?.message ?? "");
	const [lien, setLien] = useState(campagne?.deepLink ?? "");
	const [segment, setSegment] = useState(campagne?.segmentId ?? "");

	const titreTropLong = titre.length > 40;
	const messageTropLong = message.length > 120;

	return (
		<Modal
			title={campagne ? "Modifier la notification" : "Nouvelle notification"}
			onClose={onClose}
			width={560}
		>
			{/* Aperçu : un bandeau de notification tel qu'il s'affichera. */}
			<div
				style={{
					background: "var(--a-surface-2)",
					border: "1px solid var(--a-border)",
					borderRadius: 12,
					padding: 12,
					marginBottom: 16,
				}}
			>
				<div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
					<div
						style={{
							width: 32,
							height: 32,
							borderRadius: 8,
							background: "var(--a-terracotta)",
							color: "#fff",
							display: "grid",
							placeItems: "center",
							flexShrink: 0,
							fontFamily: "var(--a-font-display)",
							fontWeight: 900,
						}}
					>
						a
					</div>
					<div style={{ minWidth: 0 }}>
						<div style={{ fontSize: 12.5, fontWeight: 700 }}>
							{titre || "Titre de la notification"}
						</div>
						<div
							className="muted"
							style={{ fontSize: 11.5, lineHeight: 1.4, marginTop: 2 }}
						>
							{message || "Le corps du message apparaîtra ici."}
						</div>
					</div>
				</div>
			</div>

			<FormRow
				label="Titre"
				helper={`${titre.length} / 40`}
				required
			>
				<input
					className="aform-i"
					value={titre}
					onChange={(e) => setTitre(e.target.value)}
					placeholder="Fête des mères : -30 %"
				/>
			</FormRow>
			{titreTropLong && (
				<p style={{ fontSize: 11, color: "var(--a-warning)", marginTop: -8 }}>
					Au-delà de 40 caractères, le titre sera coupé sur la plupart des
					téléphones.
				</p>
			)}

			<FormRow label="Message" helper={`${message.length} / 120`} required>
				<textarea
					className="aform-i"
					rows={2}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					placeholder="Jusqu'à dimanche sur toute la beauté. Profites-en."
				/>
			</FormRow>
			{messageTropLong && (
				<p style={{ fontSize: 11, color: "var(--a-warning)", marginTop: -8 }}>
					Au-delà de 120 caractères, la fin du message sera masquée.
				</p>
			)}

			<FormRow
				label="Où mène la notification"
				helper="Lien interne de l'app, ex. akwaba://deals/beaute"
			>
				<input
					className="aform-i"
					value={lien}
					onChange={(e) => setLien(e.target.value)}
					placeholder="akwaba://deals/beaute"
				/>
			</FormRow>

			<FormRow label="Cible">
				<select
					className="aform-i"
					value={segment}
					onChange={(e) => setSegment(e.target.value)}
				>
					<option value="">Tous les opt-in</option>
					{(segments ?? []).map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</select>
			</FormRow>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={titre.trim().length < 3 || message.trim().length < 3 || saving}
					onClick={() =>
						onSubmit({
							title: titre.trim(),
							message: message.trim(),
							deepLink: lien.trim() || undefined,
							segmentId: segment || undefined,
						})
					}
				>
					{campagne ? "Enregistrer" : "Créer le brouillon"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
